import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { BoardGrid } from '../components/BoardGrid';
import { BoardListView, buildScheduledSessionList } from '../components/BoardListView';
import { Modal } from '../components/Modal';
import {
  SessionBoardFilters,
  FilterToggleButton,
  NoResultsMessage,
} from '../components/SessionBoardFilters';
import { useEvent } from '../hooks/useEvent';
import { fetchTimeBlocks } from '../lib/api';
import { getVotedSessionIds, getVotedProposalIds, getMySessionFilterIds } from '../lib/interestUtils';
import { getSessionBookmarks, toggleSessionBookmark } from '../lib/sessionBookmarks';
import type { Session, Template, TimeBlock } from '../types';

/** Compute field names to hide because they have <=1 option in the template. */
function singleOptionFieldNames(template: Template | null): Set<string> {
  const hidden = new Set<string>();
  if (!template) return hidden;
  if ((template.languageOptions?.length ?? 0) <= 1) hidden.add('language');
  if ((template.formatOptions?.length ?? 0) <= 1) hidden.add('format');
  if ((template.levelOptions?.length ?? 0) <= 1) hidden.add('level');
  if ((template.tagOptions?.length ?? 0) <= 1) hidden.add('tags');
  for (const field of template.fields) {
    if (hidden.has(field.name)) continue;
    if (field.type === 'select' && (field.options?.length ?? 0) <= 1) hidden.add(field.name);
  }
  return hidden;
}

export function BoardPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const {
    event,
    template,
    rooms,
    timeslots,
    sessions,
    scheduleItems,
    myVotes,
    publishedVersion,
    isLoading,
    error,
    isOrganizer,
    hasAccessCode,
    deviceId,
    nickname: currentNickname,
  } = useEvent(eventId ?? null);

  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const hiddenFields = useMemo(() => singleOptionFieldNames(template), [template]);
  const [viewMode, setViewMode] = useState<'published' | 'draft'>('published');
  const [layoutView, setLayoutView] = useState<'grid' | 'list'>('grid');
  const [filterRoomId, setFilterRoomId] = useState<string>('');
  const [filterBlockId, setFilterBlockId] = useState<string>('');
  const [filterInterested, setFilterInterested] = useState(false);
  const [timeBlocks, setTimeBlocks] = useState<TimeBlock[]>([]);
  const [bookmarkedSessionIds, setBookmarkedSessionIds] = useState<Set<string>>(new Set());

  const isLoggedIn = hasAccessCode || isOrganizer;

  useEffect(() => {
    if (eventId) {
      fetchTimeBlocks(eventId).then(setTimeBlocks).catch(() => setTimeBlocks([]));
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId && isLoggedIn) {
      getSessionBookmarks(eventId).then(setBookmarkedSessionIds);
    }
  }, [eventId, isLoggedIn]);

  const displayData = useMemo(() => {
    const rawTimeslots = viewMode === 'published' && publishedVersion
      ? publishedVersion.snapshot.timeslots
      : timeslots;
    const orderedTimeslots = [...(rawTimeslots ?? [])].sort(
      (a, b) => (a.indexInDay ?? 0) - (b.indexInDay ?? 0)
    );

    if (viewMode === 'published' && publishedVersion) {
      return {
        rooms: publishedVersion.snapshot.rooms,
        timeslots: orderedTimeslots,
        sessions: publishedVersion.snapshot.sessions,
        scheduleItems: publishedVersion.snapshot.schedule.map((s, i) => ({
          id: `pub-${i}`,
          eventId: eventId!,
          ...s,
        })),
      };
    }

    return {
      rooms,
      timeslots: orderedTimeslots,
      sessions,
      scheduleItems,
    };
  }, [viewMode, publishedVersion, rooms, timeslots, sessions, scheduleItems, eventId]);

  const votedSessionIds = useMemo(
    () => getVotedSessionIds(myVotes, displayData.sessions),
    [myVotes, displayData.sessions]
  );

  const votedProposalIds = useMemo(() => getVotedProposalIds(myVotes), [myVotes]);

  const mySessionFilterIds = useMemo(
    () => getMySessionFilterIds(votedSessionIds, bookmarkedSessionIds),
    [votedSessionIds, bookmarkedSessionIds]
  );

  const timeBlockOptions = useMemo(() => {
    if (timeBlocks.length <= 1) return [];

    const fmt = (d: string) =>
      new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return [...timeBlocks]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.startTs.localeCompare(b.startTs))
      .map((block) => ({
        id: block.id,
        label: block.label
          ? `${block.label} (${fmt(block.startTs)} – ${fmt(block.endTs)})`
          : `${fmt(block.startTs)} – ${fmt(block.endTs)}`,
      }));
  }, [timeBlocks]);

  const filteredTimeslots = useMemo(() => {
    if (!filterBlockId) return displayData.timeslots;

    const block = timeBlocks.find((b) => b.id === filterBlockId);
    if (!block) return displayData.timeslots;

    return displayData.timeslots.filter((ts) => {
      if (ts.isBreak) return false;
      if (ts.blockId) return ts.blockId === filterBlockId;
      return ts.startTs >= block.startTs && ts.endTs <= block.endTs;
    });
  }, [displayData.timeslots, filterBlockId, timeBlocks]);

  const filteredRooms = useMemo(
    () => (filterRoomId ? displayData.rooms.filter((r) => r.id === filterRoomId) : displayData.rooms),
    [displayData.rooms, filterRoomId]
  );

  const hasActiveFilters = !!(filterBlockId || filterRoomId || filterInterested);

  const visibleSessions = useMemo(
    () =>
      buildScheduledSessionList(
        filteredRooms,
        filteredTimeslots,
        displayData.sessions,
        displayData.scheduleItems,
        { interestFilter: filterInterested, filterSessionIds: mySessionFilterIds }
      ),
    [
      filteredRooms,
      filteredTimeslots,
      displayData.sessions,
      displayData.scheduleItems,
      filterInterested,
      mySessionFilterIds,
    ]
  );

  const totalScheduledCount = useMemo(
    () =>
      buildScheduledSessionList(
        filteredRooms,
        filteredTimeslots,
        displayData.sessions,
        displayData.scheduleItems
      ).length,
    [filteredRooms, filteredTimeslots, displayData.sessions, displayData.scheduleItems]
  );

  const clearFilters = useCallback(() => {
    setFilterBlockId('');
    setFilterRoomId('');
    setFilterInterested(false);
  }, []);

  const handleToggleBookmark = useCallback(
    async (sessionId: string) => {
      if (!eventId) return;
      await toggleSessionBookmark(eventId, sessionId);
      const updated = await getSessionBookmarks(eventId);
      setBookmarkedSessionIds(updated);
    },
    [eventId]
  );

  if (isLoading) {
    return (
      <Layout title={event?.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary-200 border-t-primary-400 rounded-full animate-spin" />
            <p className="mt-2 text-gray-500">Loading board...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={event?.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            {error}
          </div>
        </div>
      </Layout>
    );
  }

  const hasPublished = !!publishedVersion;
  const hasData = displayData.rooms.length > 0 && displayData.timeslots.length > 0;
  const showEmptyFiltered = hasData && totalScheduledCount > 0 && visibleSessions.length === 0;

  return (
    <Layout title={event?.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
      <div className="px-4 py-8">
        <div className="max-w-7xl mx-auto mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Session Board</h1>
              {hasPublished && (
                <p className="text-sm text-gray-500">
                  Version {publishedVersion.versionNum} - Published{' '}
                  {new Date(publishedVersion.publishedAt).toLocaleString()}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {isLoggedIn && (
                <div className="flex gap-1 rounded-lg border border-gray-200 p-0.5">
                  <button
                    type="button"
                    onClick={() => setLayoutView('grid')}
                    className={`btn btn-sm ${layoutView === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
                    aria-pressed={layoutView === 'grid'}
                  >
                    Schedule
                  </button>
                  <button
                    type="button"
                    onClick={() => setLayoutView('list')}
                    className={`btn btn-sm ${layoutView === 'list' ? 'btn-primary' : 'btn-ghost'}`}
                    aria-pressed={layoutView === 'list'}
                  >
                    List
                  </button>
                </div>
              )}
              {isOrganizer && hasPublished && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('published')}
                    className={`btn btn-sm ${viewMode === 'published' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    Published
                  </button>
                  <button
                    onClick={() => setViewMode('draft')}
                    className={`btn btn-sm ${viewMode === 'draft' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    Draft
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {hasData && (
          <div className="max-w-7xl mx-auto">
            <SessionBoardFilters>
              <div className="flex flex-wrap items-center gap-2">
                {timeBlockOptions.length > 0 && (
                  <select
                    className="select text-sm"
                    value={filterBlockId}
                    onChange={(e) => setFilterBlockId(e.target.value)}
                    aria-label="Filter by time block"
                  >
                    <option value="">All Time Blocks</option>
                    {timeBlockOptions.map((block) => (
                      <option key={block.id} value={block.id}>{block.label}</option>
                    ))}
                  </select>
                )}
                {displayData.rooms.length > 1 && (
                  <select
                    className="select text-sm"
                    value={filterRoomId}
                    onChange={(e) => setFilterRoomId(e.target.value)}
                    aria-label="Filter by room"
                  >
                    <option value="">All Rooms</option>
                    {displayData.rooms.map((room) => (
                      <option key={room.id} value={room.id}>{room.name}</option>
                    ))}
                  </select>
                )}
                {isLoggedIn && (
                  <FilterToggleButton
                    active={filterInterested}
                    onClick={() => setFilterInterested(!filterInterested)}
                    ariaLabel="Filter sessions you showed interest in or marked as going"
                  >
                    My Interests
                  </FilterToggleButton>
                )}
              </div>
            </SessionBoardFilters>

            {totalScheduledCount > 0 && (
              <p className="text-sm text-gray-500 mb-4">
                Showing {visibleSessions.length} of {totalScheduledCount} sessions
              </p>
            )}
          </div>
        )}

        {!hasData ? (
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12 rounded-lg" style={{ background: '#FFFDF5' }}>
              <p className="text-gray-500">
                {!hasPublished
                  ? 'No schedule has been published yet.'
                  : 'No rooms or timeslots configured.'}
              </p>
              {isOrganizer && (
                <Link
                  to={`/event/${eventId}/admin`}
                  className="btn btn-primary mt-4"
                >
                  Go to Admin Panel
                </Link>
              )}
            </div>
          </div>
        ) : showEmptyFiltered ? (
          <div className="max-w-7xl mx-auto">
            <NoResultsMessage
              hasItems={totalScheduledCount > 0}
              emptyMessage="No sessions scheduled yet."
              filteredMessage="No sessions match your filters."
              onClearFilters={hasActiveFilters ? clearFilters : undefined}
            />
          </div>
        ) : layoutView === 'list' && isLoggedIn ? (
          <div className="max-w-7xl mx-auto">
            <BoardListView
              rooms={filteredRooms}
              timeslots={filteredTimeslots}
              sessions={displayData.sessions}
              scheduleItems={displayData.scheduleItems}
              votedSessionIds={votedSessionIds}
              bookmarkedSessionIds={bookmarkedSessionIds}
              interestFilter={filterInterested}
              filterSessionIds={mySessionFilterIds}
              canBookmark={isLoggedIn}
              onToggleBookmark={handleToggleBookmark}
              onSessionClick={(sessionId) => {
                const session = displayData.sessions.find((s) => s.id === sessionId);
                if (session) setSelectedSession(session);
              }}
            />
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            <BoardGrid
              rooms={filteredRooms}
              timeslots={filteredTimeslots}
              sessions={displayData.sessions}
              scheduleItems={displayData.scheduleItems}
              votedSessionIds={votedSessionIds}
              bookmarkedSessionIds={bookmarkedSessionIds}
              interestFilter={filterInterested}
              filterSessionIds={mySessionFilterIds}
              canBookmark={isLoggedIn}
              onToggleBookmark={handleToggleBookmark}
              onSessionClick={(sessionId) => {
                const session = displayData.sessions.find((s) => s.id === sessionId);
                if (session) setSelectedSession(session);
              }}
            />
          </div>
        )}

        <div className="max-w-7xl mx-auto mt-6">
          <details className="text-sm text-gray-500">
            <summary className="cursor-pointer hover:text-gray-700">
              Keyboard Navigation
            </summary>
            <div className="mt-2 p-4 bg-gray-50 rounded-lg">
              <ul className="space-y-1">
                <li><kbd className="px-1 bg-gray-200 rounded">Tab</kbd> - Move between cells</li>
                <li><kbd className="px-1 bg-gray-200 rounded">Enter</kbd> or <kbd className="px-1 bg-gray-200 rounded">Space</kbd> - Select a session</li>
                <li><kbd className="px-1 bg-gray-200 rounded">Escape</kbd> - Close dialogs</li>
              </ul>
            </div>
          </details>
        </div>

        {selectedSession && (
          <SessionDetailModal
            session={selectedSession}
            onClose={() => setSelectedSession(null)}
            hiddenFields={hiddenFields}
            voted={votedSessionIds.has(selectedSession.id)}
            votedProposalIds={votedProposalIds}
            bookmarked={bookmarkedSessionIds.has(selectedSession.id)}
            canBookmark={isLoggedIn}
            onToggleBookmark={
              isLoggedIn
                ? () => handleToggleBookmark(selectedSession.id)
                : undefined
            }
          />
        )}
      </div>
    </Layout>
  );
}

function SessionDetailModal({
  session,
  onClose,
  hiddenFields,
  voted,
  votedProposalIds,
  bookmarked,
  canBookmark,
  onToggleBookmark,
}: {
  session: Session;
  onClose: () => void;
  hiddenFields?: Set<string>;
  voted?: boolean;
  votedProposalIds?: Set<string>;
  bookmarked?: boolean;
  canBookmark?: boolean;
  onToggleBookmark?: () => void;
}) {
  const [showProposals, setShowProposals] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(!!bookmarked);
  const presenters = session.people.filter((p) => p.role === 'presenter');
  const copresenters = session.people.filter((p) => p.role === 'copresenter');
  const proposalInterest = session.proposalVoteCount ?? 0;
  const sessionInterest = session.voteCount ?? 0;
  const totalInterest = proposalInterest + sessionInterest;

  useEffect(() => {
    setIsBookmarked(!!bookmarked);
  }, [bookmarked]);

  const handleBookmarkClick = () => {
    onToggleBookmark?.();
    setIsBookmarked((prev) => !prev);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={session.title}>
      <div className="space-y-4">
        {presenters.length > 0 && (
          <div>
            <span className="text-sm text-gray-500">Presenter(s)</span>
            <p className="font-medium">
              {presenters.map((p) => p.nickname).join(', ')}
            </p>
          </div>
        )}

        {copresenters.length > 0 && (
          <div>
            <span className="text-sm text-gray-500">Co-presenter(s)</span>
            <p>{copresenters.map((p) => p.nickname).join(', ')}</p>
          </div>
        )}

        {session.description && (
          <div>
            <span className="text-sm text-gray-500">Description</span>
            <p className="whitespace-pre-wrap">{session.description}</p>
          </div>
        )}

        {session.fields && Object.entries(session.fields).some(([k, v]) => v && !hiddenFields?.has(k)) && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(session.fields).map(([key, value]) => (
              value && !hiddenFields?.has(key) && (
                <span key={key} className="badge badge-gray">
                  {value}
                </span>
              )
            ))}
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              <span className="text-xl font-bold text-gray-900">{totalInterest}</span>
            </div>
            <span className="text-gray-500">interested attendees</span>
          </div>
          {proposalInterest > 0 && sessionInterest > 0 && (
            <p className="text-xs text-gray-400">
              {proposalInterest} from session cards + {sessionInterest} direct session interest
            </p>
          )}
          {proposalInterest > 0 && sessionInterest === 0 && (
            <p className="text-xs text-gray-400">
              {proposalInterest} from session card interest
            </p>
          )}
          {voted && (
            <div className="voted-badge mt-2">
              <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              You showed interest
            </div>
          )}
          {canBookmark && onToggleBookmark && (
            <button
              type="button"
              onClick={handleBookmarkClick}
              className={`btn btn-sm mt-2 ${isBookmarked ? 'btn-primary' : 'btn-secondary'}`}
              aria-pressed={isBookmarked}
            >
              <svg className="w-4 h-4 mr-1.5 inline-block" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              {isBookmarked ? 'Want to go — saved' : 'Want to go to this session'}
            </button>
          )}
        </div>

        {session.proposals.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={() => setShowProposals(!showProposals)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors w-full"
            >
              <svg
                className={`w-4 h-4 transform transition-transform ${showProposals ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span>
                Based on {session.proposals.length} session card{session.proposals.length !== 1 ? 's' : ''}
              </span>
            </button>
            {showProposals && (
              <ul className="mt-3 space-y-2 pl-6">
                {session.proposals.map((p) => {
                  const userInterested = votedProposalIds?.has(p.id);
                  return (
                    <li
                      key={p.id}
                      className={`text-sm border-l-2 pl-3 py-1 ${
                        userInterested ? 'border-green-400 bg-green-50/50 rounded-r' : 'border-primary-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-900">{p.title}</p>
                          <p className="text-gray-500">by {p.createdByNickname}</p>
                        </div>
                        {userInterested && (
                          <span className="voted-badge flex-shrink-0" title="You showed interest in this card">
                            <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Interested
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
